// ─────────────────────────────────────────────────────────────────────────────
// SHARED HYPE CATALOG
//
// One list, used by every "Hype" game (The Drop, Shred, and anything later).
// Hype moves fast — this list will feel dated in months. To refresh:
//
//   • New colorway of a silhouette we already draw  →  add one row below,
//     pick an `art` template and set `colors`. No new code. Redeploy (~1 min).
//   • Genuinely new silhouette / garment  →  add a template fn in
//     src/client/art.ts, then add rows. (Rare — hype churn is mostly colorways.)
//
// Bump `CATALOG_REVIEWED` when you do a pass so we know how stale it is.
// Later: this can move to Firestore + a phone-friendly admin page so edits
// don't need a deploy — the art templates would stay in code either way.
// ─────────────────────────────────────────────────────────────────────────────

export const CATALOG_REVIEWED = "2026-08-31";

export type ArtId =
  | "j1" | "aj4" | "dunk" | "yz350" | "af1" | "foam"
  | "hoodie" | "shark" | "tee" | "sweats" | "cross";

/** grail = chase item (rare, worth the most), hype = solid, gen = general release,
 *  fake = a replica trap (negative value, only surfaces in games that want it). */
export type Tier = "grail" | "hype" | "gen" | "fake";

export interface CatalogItem {
  id: string;
  brand: string;
  name: string;
  colorway: string;
  art: ArtId;
  colors: Record<string, string>;
  /** stylized resale in USD — powers The Drop scoring; ordering matters more than precision */
  value: number;
  tier: Tier;
  /** Shred: hit points to destroy it. Omit to use the per-art default. */
  toughness?: number;
}

// palette shorthands
const WHITE = "#f3f3f3", BONE = "#e9e3d4", BLACK = "#161616", RED = "#c9302c",
  UNIV_BLUE = "#2f5fbf", GUM = "#c7a24a", OLIVE = "#5a5a3c", CREAM = "#efe6cf",
  GREY = "#d6d6d6", TAN = "#8c8175", NAVY = "#22314f", PINK = "#e6a5b8";

export const CATALOG: CatalogItem[] = [
  // ---- grails ----
  { id: "ts-aj1-low", brand: "Travis Scott", name: "Air Jordan 1 Low", colorway: "Reverse Mocha",
    art: "j1", value: 1300, tier: "grail",
    colors: { out: BLACK, mid: WHITE, heel: "#8a7d63", collar: "#8a7d63", tongue: WHITE, eye: "#4a3f2c", vamp: WHITE, toe: "#8a7d63", swoosh: "#4a3f2c", lace: BLACK, wings: BLACK } },
  { id: "ow-aj1", brand: "Nike x Off-White", name: "Air Jordan 1", colorway: "Chicago",
    art: "j1", value: 3200, tier: "grail",
    colors: { out: BLACK, mid: "#e7e2d0", heel: RED, collar: RED, tongue: WHITE, eye: RED, vamp: WHITE, toe: RED, swoosh: BLACK, lace: "#d9d3bf", wings: "#ff7a00" } },
  { id: "sb-dunk-whatthe", brand: "Nike SB", name: "Dunk Low", colorway: "What The Dunk",
    art: "dunk", value: 2600, tier: "grail",
    colors: { out: GUM, mid: CREAM, heel: RED, collar: UNIV_BLUE, eye: "#e0b64a", vamp: "#2f8f4f", toe: PINK, swoosh: BLACK, lace: WHITE } },
  { id: "sb-dunk-stpat", brand: "Nike SB", name: "Dunk Low", colorway: "St. Patrick's Day",
    art: "dunk", value: 1500, tier: "grail",
    colors: { out: GUM, mid: CREAM, heel: "#2f6b3f", collar: "#2f6b3f", eye: CREAM, vamp: CREAM, toe: "#2f6b3f", swoosh: "#2f6b3f", lace: CREAM } },
  { id: "ch-cemetery-tee", brand: "Chrome Hearts", name: "Cemetery Cross Tee", colorway: "Black",
    art: "tee", value: 750, tier: "grail",
    colors: { body: BLACK, logo: "#d8d8d8" } },
  { id: "wb-samba", brand: "adidas x Wales Bonner", name: "Samba", colorway: "Cream Collegiate",
    art: "af1", value: 500, tier: "grail",
    colors: { out: GUM, mid: CREAM, collar: "#7a2a2a", eye: CREAM, vamp: CREAM, swoosh: "#7a2a2a", lace: CREAM } },

  // ---- hype ----
  { id: "aj1-chicago", brand: "Jordan", name: "Air Jordan 1 High", colorway: "Chicago",
    art: "j1", value: 820, tier: "hype",
    colors: { out: BLACK, mid: WHITE, heel: RED, collar: RED, tongue: WHITE, eye: RED, vamp: WHITE, toe: RED, swoosh: BLACK, lace: BLACK, wings: BLACK } },
  { id: "aj1-bredtoe", brand: "Jordan", name: "Air Jordan 1 High", colorway: "Bred Toe",
    art: "j1", value: 470, tier: "hype",
    colors: { out: BLACK, mid: WHITE, heel: BLACK, collar: BLACK, tongue: WHITE, eye: RED, vamp: WHITE, toe: RED, swoosh: BLACK, lace: BLACK, wings: BLACK } },
  { id: "aj1-royal", brand: "Jordan", name: "Air Jordan 1 High", colorway: "Royal",
    art: "j1", value: 400, tier: "hype",
    colors: { out: BLACK, mid: WHITE, heel: UNIV_BLUE, collar: UNIV_BLUE, tongue: BLACK, eye: BLACK, vamp: BLACK, toe: BLACK, swoosh: UNIV_BLUE, lace: BLACK, wings: BLACK } },
  { id: "aj4-bred", brand: "Jordan", name: "Air Jordan 4", colorway: "Bred",
    art: "aj4", value: 420, tier: "hype",
    colors: { out: BLACK, mid: WHITE, upper: BLACK, cage: BLACK, wing: RED, sole: "#8a8a8a" } },
  { id: "aj4-whitecement", brand: "Jordan", name: "Air Jordan 4", colorway: "White Cement",
    art: "aj4", value: 260, tier: "hype",
    colors: { out: "#9a9a9a", mid: WHITE, upper: WHITE, cage: "#cfcfcf", wing: BLACK, sole: GREY } },
  { id: "yz350-zebra", brand: "adidas Yeezy", name: "Boost 350 V2", colorway: "Zebra",
    art: "yz350", value: 270, tier: "hype",
    colors: { boost: "#efe9dd", boostL: "#8a8172", knit: "#ece6d8", stripe: BLACK, lace: "#cfc7b6", heelT: RED } },
  { id: "yz350-bred", brand: "adidas Yeezy", name: "Boost 350 V2", colorway: "Bred",
    art: "yz350", value: 330, tier: "hype",
    colors: { boost: "#d6d6d6", boostL: "#9a9a9a", knit: BLACK, stripe: "#a83636", lace: "#333", heelT: BLACK } },
  { id: "bape-shark", brand: "BAPE", name: "Shark Full Zip Hoodie", colorway: "Green Camo",
    art: "shark", value: 430, tier: "hype",
    colors: { body: "#3f6d3f", hood: "#264a26", logo: "#d9c98a" } },
  { id: "fog-hoodie", brand: "Fear of God", name: "7th Collection Hoodie", colorway: "Vintage Black",
    art: "hoodie", value: 400, tier: "hype",
    colors: { body: "#2a2a2c", hood: "#2a2a2c", logo: "#2a2a2c" } },
  { id: "sp5der-hoodie", brand: "Sp5der", name: "Web Hoodie", colorway: "Slime Green",
    art: "hoodie", value: 290, tier: "hype",
    colors: { body: "#8bd646", hood: "#6fae37", logo: "#1c2b12" } },
  { id: "supreme-bogo", brand: "Supreme", name: "Box Logo Hoodie", colorway: "Ash Grey",
    art: "hoodie", value: 520, tier: "hype",
    colors: { body: "#b8b8b8", hood: "#b8b8b8", logo: RED } },
  { id: "denimtears-hoodie", brand: "Denim Tears", name: "Cotton Wreath Hoodie", colorway: "Grey",
    art: "hoodie", value: 340, tier: "hype",
    colors: { body: "#9a9a9a", hood: "#9a9a9a", logo: "#3a5fbf" } },
  { id: "yz700-wave", brand: "adidas Yeezy", name: "700 Wave Runner", colorway: "OG",
    art: "yz350", value: 380, tier: "hype",
    colors: { boost: "#e6e0d2", boostL: "#9a8f78", knit: "#8a8f96", stripe: "#c98a4a", lace: "#cfc7b6", heelT: "#6b7f5a" } },

  // ---- general release ----
  { id: "dunk-panda", brand: "Nike", name: "Dunk Low", colorway: "Panda",
    art: "dunk", value: 120, tier: "gen",
    colors: { out: GREY, mid: WHITE, heel: BLACK, collar: BLACK, eye: WHITE, vamp: WHITE, toe: WHITE, swoosh: BLACK, lace: BLACK } },
  { id: "af1-triplewhite", brand: "Nike", name: "Air Force 1 Low", colorway: "Triple White",
    art: "af1", value: 110, tier: "gen",
    colors: { out: "#e9e9e9", mid: "#fbfbfb", collar: "#fbfbfb", eye: "#fbfbfb", vamp: "#fbfbfb", swoosh: "#fbfbfb", lace: "#fbfbfb" } },
  { id: "foam-onyx", brand: "adidas Yeezy", name: "Foam Runner", colorway: "Onyx",
    art: "foam", value: 105, tier: "gen",
    colors: { body: "#1b1b1b", sole: "#0b0b0b", hole: "#0c0e13" } },
  { id: "nb550-white", brand: "New Balance", name: "550", colorway: "White Green",
    art: "af1", value: 120, tier: "gen",
    colors: { out: "#e3e3e3", mid: WHITE, collar: "#2f6b3f", eye: WHITE, vamp: WHITE, swoosh: "#2f6b3f", lace: WHITE } },
  { id: "essentials-hoodie", brand: "Fear of God Essentials", name: "Hoodie", colorway: "Taupe",
    art: "hoodie", value: 120, tier: "gen",
    colors: { body: TAN, hood: TAN, logo: "#39332d" } },
  { id: "samba-og", brand: "adidas", name: "Samba OG", colorway: "Black White Gum",
    art: "af1", value: 100, tier: "gen",
    colors: { out: GUM, mid: WHITE, collar: BLACK, eye: WHITE, vamp: BLACK, swoosh: WHITE, lace: WHITE } },
  { id: "denimtears-sweats", brand: "Denim Tears", name: "Cotton Wreath Sweatpants", colorway: "Navy",
    art: "sweats", value: 300, tier: "hype",
    colors: { body: NAVY, logo: "#c9c2a8" } },
  { id: "ch-ring", brand: "Chrome Hearts", name: "Dagger Ring", colorway: "Sterling",
    art: "cross", value: 520, tier: "hype", toughness: 220,
    colors: { metal: "#cfd3da", shade: "#8a8f99" } },

  // ---- fakes (replica traps — used by games that opt in) ----
  { id: "fake-aj1-bred", brand: "“Air Jordan 1”", name: "High (rep)", colorway: "Bred · muddy",
    art: "j1", value: -420, tier: "fake",
    colors: { out: "#1c1c1c", mid: "#dcdcdc", heel: "#1c1c1c", collar: "#1c1c1c", tongue: "#1c1c1c", eye: "#1c1c1c", vamp: "#1c1c1c", toe: "#1c1c1c", swoosh: "#7a1f1f", lace: "#1c1c1c", wings: "#7a1f1f" } },
  { id: "fake-yz350", brand: "“Yeezy 350”", name: "V2 (rep)", colorway: "Zebra · off",
    art: "yz350", value: -320, tier: "fake",
    colors: { boost: "#d8d0bd", boostL: "#7a7263", knit: "#d8d0bd", stripe: "#4a4a4a", lace: "#b7ad97", heelT: "#7a1f1f" } },
  { id: "fake-supreme-bogo", brand: "“Supreme”", name: "Box Logo (rep)", colorway: "wrong red",
    art: "hoodie", value: -300, tier: "fake",
    colors: { body: "#8f8f8f", hood: "#8f8f8f", logo: "#8f1f2a" } },
  { id: "fake-essentials", brand: "“Essentials”", name: "Hoodie (rep)", colorway: "thick logo",
    art: "hoodie", value: -220, tier: "fake",
    colors: { body: "#7f766a", hood: "#7f766a", logo: "#000000" } },
];

export const ITEMS_BY_ID: Record<string, CatalogItem> =
  Object.fromEntries(CATALOG.map((i) => [i.id, i]));

/** default Shred hit points by art family */
export const ART_TOUGHNESS: Record<ArtId, number> = {
  j1: 140, aj4: 150, dunk: 135, yz350: 120, af1: 135, foam: 110,
  hoodie: 100, shark: 115, tee: 65, sweats: 90, cross: 210,
};

export function toughnessOf(item: CatalogItem): number {
  return item.toughness ?? ART_TOUGHNESS[item.art] ?? 120;
}

export function pool(tiers: Tier[]): CatalogItem[] {
  return CATALOG.filter((i) => tiers.includes(i.tier));
}

/** display title, de-duplicating brand words already in the model name */
export function titleOf(i: CatalogItem): string {
  const key = i.brand.toLowerCase().replace(/[^a-z ]/g, "").split(" ")[0] || "";
  return key && i.name.toLowerCase().includes(key) ? i.name : `${i.brand} ${i.name}`;
}
